import { createZip, type ZipEntry } from './zip';

/**
 * Download a creative asset. Fetches the (usually cross-origin, CDN-hosted) image as a blob so the
 * browser saves a real file; if the CDN blocks a cross-origin fetch, falls back to opening the URL
 * in a new tab so the owner can still save it manually.
 */
export const downloadAsset = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error('fetch failed');
    const blob = await response.blob();
    saveBlob(blob, filename);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
};

/**
 * The extension for a (possibly query-tailed) asset url. Recognises the still-image formats plus
 * video (`mp4` / `webm`) so a downloaded clip keeps its real extension; falls back to `fallback`
 * (`png` by default, e.g. `mp4` for a known-video download) when the url carries none.
 */
export const assetExt = (url: string, fallback = 'png'): string =>
  (url.split('?')[0].match(/\.(png|jpe?g|webp|gif|svg|mp4|webm)$/i)?.[1] || fallback).toLowerCase();

/** A filesystem-safe file name for a downloaded creative. `fallbackExt` is used when the url has no
 *  recognisable extension (pass `mp4` for a video download so it never defaults to `png`). */
export const assetFilename = (brand: string, label: string, url: string, fallbackExt = 'png'): string => {
  const slug = `${brand || 'creative'}-${label || 'asset'}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'creative'}.${assetExt(url, fallbackExt)}`;
};

/** Save a Blob to the owner's downloads as `filename`.
 *  Also used by Settings > Secure your brand for the signed contract. */
export const saveBlob = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

/**
 * Bundle several assets into one ZIP and download it. Each entry names its file inside the archive.
 * Images the browser can't fetch (a CORS-blocked CDN) are skipped; returns how many were actually
 * zipped so the caller can fall back (e.g. open the single hero) when nothing could be fetched.
 */
export const downloadAssetsZip = async (
  entries: ReadonlyArray<{ name: string; url: string }>,
  zipName: string,
): Promise<number> => {
  const fetched = await Promise.all(
    entries.map(async (entry): Promise<ZipEntry | null> => {
      try {
        const response = await fetch(entry.url, { mode: 'cors' });
        if (!response.ok) return null;
        return { name: entry.name, data: new Uint8Array(await response.arrayBuffer()) };
      } catch {
        return null;
      }
    }),
  );
  const files = fetched.filter((file): file is ZipEntry => file !== null);
  if (files.length === 0) return 0;
  saveBlob(createZip(files), zipName);
  return files.length;
};
